import { useEffect, useMemo, useState } from 'react';
import { DashboardLayout } from '@/layout/DashboardLayout';
import { ConfirmDialog } from '@/design-system';
import { useErrorToast } from '@/core/errors/useErrorToast';
import type { Affiliate, AffiliateWithStats } from './api/affiliates.service';
import { useAffiliatesListQuery } from './hooks/useAffiliatesListQuery';
import { useAffiliateMutations } from './hooks/useAffiliateMutations';
import { AffiliateCouponsDialog } from './components/AffiliateCouponsDialog';
import { AffiliateCreateDialog } from './components/AffiliateCreateDialog';
import { AffiliateEditDialog } from './components/AffiliateEditDialog';
import { AffiliateTable, type AffiliateTableHandlers } from './components/AffiliateTable';

export function AffiliatesPage() {
  const onErrorToast = useErrorToast();
  const { createAffiliate, updateAffiliate, deleteAffiliate, createAffiliateCoupon } = useAffiliateMutations();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCouponsDialog, setShowCouponsDialog] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState<Affiliate | null>(null);
  const [affiliateToDelete, setAffiliateToDelete] = useState<Affiliate | null>(null);

  const { data: affiliates = [], isLoading, isError, error } = useAffiliatesListQuery(true);

  useEffect(() => {
    if (isError && error) onErrorToast(error, 'Erro ao carregar afiliados');
  }, [isError, error, onErrorToast]);

  const tableHandlers = useMemo<AffiliateTableHandlers>(
    () => ({
      onCreate: () => setShowCreateDialog(true),
      onEdit: (a) => {
        setSelectedAffiliate(a);
        setShowEditDialog(true);
      },
      onCoupons: (a) => {
        setSelectedAffiliate(a);
        setShowCouponsDialog(true);
      },
      onDelete: (a) => setAffiliateToDelete(a),
    }),
    []
  );

  return (
    <DashboardLayout title="Afiliados">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Afiliados/Parceiros: criam cupons do tipo AFFILIATE. Empresas que usam o cupom ficam vinculadas
          (referred_by).
        </p>

        <AffiliateTable
          affiliates={affiliates as AffiliateWithStats[]}
          loading={isLoading}
          handlers={tableHandlers}
        />
      </div>

      <AffiliateCreateDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        createAffiliate={createAffiliate}
      />

      <AffiliateEditDialog
        isOpen={showEditDialog}
        affiliate={selectedAffiliate}
        onClose={() => {
          setShowEditDialog(false);
          setSelectedAffiliate(null);
        }}
        updateAffiliate={updateAffiliate}
      />

      <AffiliateCouponsDialog
        isOpen={showCouponsDialog}
        affiliate={selectedAffiliate}
        onClose={() => {
          setShowCouponsDialog(false);
          setSelectedAffiliate(null);
        }}
        createAffiliateCoupon={createAffiliateCoupon}
      />

      <ConfirmDialog
        isOpen={!!affiliateToDelete}
        onClose={() => setAffiliateToDelete(null)}
        onConfirm={() => {
          if (!affiliateToDelete) return;
          void deleteAffiliate.mutateAsync(affiliateToDelete.id).then(() => setAffiliateToDelete(null));
        }}
        title="Excluir afiliado"
        message={
          affiliateToDelete
            ? `Excluir "${affiliateToDelete.name}"? Só é possível se não houver cupons vinculados nem empresas indicadas por este afiliado.`
            : ''
        }
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="danger"
        isLoading={deleteAffiliate.isPending}
      />
    </DashboardLayout>
  );
}
